import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Typography, Select, Row, Col, Input, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const { Title } = Typography;

export default function OtReport() {
    const { authData, backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [otData, setOtData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    /* ---------------- FETCH MASTER DATA ---------------- */

    useEffect(() => {
        if (authData?.access) {
            fetchOrganisations();
            fetchOTRecords(1, pageSize);
        }
    }, [authData?.access]);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    const fetchOTRecords = async (page = 1, size = 10, overrideFilters = {}) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append("page", page);
            params.append("page_size", size);
            params.append("status", "APPROVED");
            params.append("view_mode", "admin");

            const org = "org" in overrideFilters ? overrideFilters.org : selectedOrg;
            const dept = "dept" in overrideFilters ? overrideFilters.dept : selectedDept;
            const desig = "desig" in overrideFilters ? overrideFilters.desig : selectedDesig;
            const search = "search" in overrideFilters ? overrideFilters.search : searchText;

            if (org) params.append("organisation", org);
            if (dept) params.append("department", dept);
            if (desig) params.append("designation", desig);
            
            if (search && search.trim()) {
                const text = search.trim();
                if (/^[A-Za-z]+[0-9]+$/.test(text)) {
                    params.append("employee_code", text);
                } else {
                    params.append("employee_name", text);
                }
            }

            const res = await axios.get(
                `${backUrl}/api/payroll/manager/ot-requests/?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            
            setOtData(res.data.results || []);
            setTotalRecords(res.data.count || 0);
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching OT records:", error);
            message.error("Failed to fetch OT records");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- FILTER HANDLERS ---------------- */

    const handleOrgChange = (value) => {
        setSelectedOrg(value);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDepartments(value);
        
        fetchOTRecords(1, pageSize, {
            org: value,
            dept: null,
            desig: null
        });
    };

    const handleDeptChange = (value) => {
        setSelectedDept(value);
        setSelectedDesig(null);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDesignations(value);

        fetchOTRecords(1, pageSize, {
            dept: value,
            desig: null
        });
    };

    const handleDesigChange = (value) => {
        setSelectedDesig(value);
        setCurrentPage(1);

        fetchOTRecords(1, pageSize, {
            desig: value
        });
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchText(val);
        if (!val.trim()) {
            fetchOTRecords(1, pageSize, { search: "" });
        }
    };

    const handleClearFilters = () => {
        setSelectedOrg(null);
        setSelectedDept(null);
        setSelectedDesig(null);
        setSearchText("");
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        
        fetchOTRecords(1, pageSize, {
            org: null,
            dept: null,
            desig: null,
            search: ""
        });
    };

    /* ---------------- TABLE DATA ---------------- */

    const tableData = useMemo(() => {
        return otData.map(item => ({
            key: item.id,

            organisationId: item.employee?.organisation,
            departmentId: item.employee?.department,
            designationId: item.employee?.designation,

            organisationName: item?.employee?.organisation_name || "-",
            departmentName: item.department || "-",
            designationName: item.designation || "-",

            monthYear: dayjs(item.attendance_date).format("MMMM-YYYY"),
            empId: item?.employee?.emp_code || item?.employee?.username || "-",
            empName: item?.employee_name || `${item?.employee?.first_name || ""} ${item?.employee?.last_name || ""}`.trim() || "-",
            site: item.site_location || "-",
            inTime: item.in_time || "-",
            outTime: item.out_time || "-",
            totalDuration: item.total_duration || "-",
            approvedOt: item.approved_ot_hours?.substring(0, 5) || "-",
            otRate: item.ot_rate ?? 0,
            otAmount: item.ot_amount ?? 0,
            remarks: item.employee_remarks || "-",
            verifiedBy: item.approval_logs?.[0]?.approver_name || "-",
            approvedBy: item.approval_logs?.[item.approval_logs.length - 1]?.approver_name || "-"
        }));
    }, [otData]);

    /* ---------------- TABLE COLUMNS ---------------- */

    const columns = [
        {
            title: "S.No",
            render: (_, __, index) =>
                (currentPage - 1) * pageSize + index + 1,
            width: 70,
            align: "center"
        },
        { title: "Month–Year", dataIndex: "monthYear", align: "center" },
        { title: "Employee ID", dataIndex: "empId", align: "center" },
        { title: "Employee Name", dataIndex: "empName", align: "center" },
        { title: "Organisation", dataIndex: "organisationName", align: "center" },
        { title: "Department", dataIndex: "departmentName", align: "center" },
        { title: "Designation", dataIndex: "designationName", align: "center" },
        { title: "Site/Location", dataIndex: "site", align: "center" },
        { title: "In Time", dataIndex: "inTime", align: "center" },
        { title: "Out Time", dataIndex: "outTime", align: "center" },
        { title: "Total Duration (HH:MM)", dataIndex: "totalDuration", align: "center" },
        { title: "Approved OT Hours", dataIndex: "approvedOt", align: "center" },
        { title: "OT Rate", dataIndex: "otRate", align: "center" },
        { title: "OT Amount", dataIndex: "otAmount", align: "center" },
        { title: "Remarks", dataIndex: "remarks", align: "center" },
        { title: "Verified By", dataIndex: "verifiedBy", align: "center" },
        { title: "Approved By", dataIndex: "approvedBy", align: "center" }
    ];

    /* ---------------- EXCEL DOWNLOAD ---------------- */

    const downloadExcel = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append("page", 1);
            params.append("page_size", 10000); // Fetch all records for export
            params.append("status", "APPROVED");
            params.append("view_mode", "admin");

            if (selectedOrg) params.append("organisation", selectedOrg);
            if (selectedDept) params.append("department", selectedDept);
            if (selectedDesig) params.append("designation", selectedDesig);
            
            if (searchText.trim()) {
                const text = searchText.trim();
                if (/^[A-Za-z]+[0-9]+$/.test(text)) {
                    params.append("employee_code", text);
                } else {
                    params.append("employee_name", text);
                }
            }

            const res = await axios.get(
                `${backUrl}/api/payroll/manager/ot-requests/?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            const rawExportData = res.data.results || res.data;
            if (!rawExportData.length) {
                message.warning("No data available to export");
                return;
            }

            const excelData = rawExportData.map((row, index) => {
                const empName = row?.employee_name || `${row?.employee?.first_name || ""} ${row?.employee?.last_name || ""}`.trim() || "-";
                return {
                    "S.No": index + 1,
                    "Month–Year": dayjs(row.attendance_date).format("MMMM-YYYY"),
                    "Employee ID": row?.employee?.emp_code || row?.employee?.username || "-",
                    "Employee Name": empName,
                    "Organisation": row?.employee?.organisation_name || "-",
                    "Department": row.department || "-",
                    "Designation": row.designation || "-",
                    "Site/Location": row.site_location || "-",
                    "In Time": row.in_time || "-",
                    "Out Time": row.out_time || "-",
                    "Total Duration (HH:MM)": row.total_duration || "-",
                    "Approved OT Hours": row.approved_ot_hours?.substring(0, 5) || "-",
                    "OT Rate": row.ot_rate ?? 0,
                    "OT Amount": row.ot_amount ?? 0,
                    "Remarks": row.employee_remarks || "-",
                    "Verified By": row.approval_logs?.[0]?.approver_name || "-",
                    "Approved By": row.approval_logs?.[row.approval_logs.length - 1]?.approver_name || "-"
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "OT Report");

            // Style header row
            const range = XLSX.utils.decode_range(worksheet["!ref"]);
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const address = XLSX.utils.encode_cell({ r: 0, c: C });
                if (!worksheet[address]) continue;
                worksheet[address].s = {
                    fill: { fgColor: { rgb: "0e497b" } },
                    font: { color: { rgb: "FFFFFF" }, bold: true },
                    alignment: { horizontal: "center" }
                };
            }

            const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            saveAs(new Blob([buffer]), "OT_Report.xlsx");
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            message.error("Failed to export data");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ margin: "24px" }}>
            <Title level={4} style={{ marginBottom: 24 }}>Over Time Report</Title>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={4}>
                    <Select
                        placeholder="Select Organisation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={selectedOrg}
                        onChange={handleOrgChange}
                        style={{ width: "100%" }}
                    >
                        {organisations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={4}>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedOrg}
                        value={selectedDept}
                        onChange={handleDeptChange}
                        style={{ width: "100%" }}
                    >
                        {departments.map(dept => (
                            <Select.Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={4}>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedDept}
                        value={selectedDesig}
                        onChange={handleDesigChange}
                        style={{ width: "100%" }}
                    >
                        {designations.map(des => (
                            <Select.Option key={des.id} value={des.id}>
                                {des.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={4}>
                    <Input
                        placeholder="Search by Employee ID or Name"
                        allowClear
                        value={searchText}
                        onChange={handleSearchChange}
                        onPressEnter={() => fetchOTRecords(1, pageSize)}
                        style={{ width: "100%" }}
                    />
                </Col>

                <Col span={4}>
                    <Button
                        type="primary"
                        onClick={() => fetchOTRecords(1, pageSize)}
                        style={{ marginRight: 8, backgroundColor: "#0e497b" }}
                    >
                        Apply
                    </Button>
                    <Button onClick={handleClearFilters}>
                        Clear
                    </Button>
                </Col>

                <Col span={4} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        style={{ backgroundColor: "#0e497b" }}
                    >
                        Download Excel
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={tableData}
                loading={loading}
                bordered
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalRecords,
                    onChange: page => fetchOTRecords(page, pageSize),
                    showSizeChanger: false
                }}
                scroll={{ x: "max-content" }}
                components={{
                    header: {
                        cell: props => (
                            <th {...props} style={{ background: "#0e497b", color: "#fff", textAlign: "center" }}>
                                {props.children}
                            </th>
                        )
                    }
                }}
            />
        </div>
    );
}